import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { Slider, Arrow, CollectionCard } from '@tc-packages/tc-common'

const DEFAULT_SLIDES_TO_SHOW = 5
const DEBOUNCE_DELAY = 150

const debounce = (func, wait) => {
  let timeout
  return function executedFunction(...args) {
    clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

const useResponsiveSlides = (sliderOptions) => {
  const [currentSlidesToShow, setCurrentSlidesToShow] = useState(() => {
    return sliderOptions.slidesToShow || DEFAULT_SLIDES_TO_SHOW
  })

  const getSlidesToShow = useCallback(() => {
    const defaultSlidesToShow = sliderOptions.slidesToShow || DEFAULT_SLIDES_TO_SHOW

    if (typeof window === 'undefined') {
      return defaultSlidesToShow
    }

    const windowWidth = window.innerWidth
    const responsive = sliderOptions.responsive || []
    const sortedResponsive = [...responsive].sort((a, b) => a.breakpoint - b.breakpoint)

    for (const breakpoint of sortedResponsive) {
      if (windowWidth <= breakpoint.breakpoint) {
        const settings = typeof breakpoint.settings === 'string'
          ? JSON.parse(breakpoint.settings)
          : breakpoint.settings
        return settings.slidesToShow || defaultSlidesToShow
      }
    }

    return defaultSlidesToShow
  }, [sliderOptions.slidesToShow, sliderOptions.responsive])

  useEffect(() => {
    const updateSlidesToShow = () => {
      const newSlidesToShow = getSlidesToShow()
      setCurrentSlidesToShow(newSlidesToShow)
    }

    const debouncedUpdate = debounce(updateSlidesToShow, DEBOUNCE_DELAY)

    updateSlidesToShow()
    window.addEventListener('resize', debouncedUpdate)

    return () => {
      window.removeEventListener('resize', debouncedUpdate)
    }
  }, [getSlidesToShow])

  return currentSlidesToShow
}

export const CollectionSlider = ({
                                   products,
                                   sliderOptions = {},
                                   additionalClasses = '',
                                   containerClass = 'cardSlider_container container',
                                   id,
                                 }) => {
  const currentSlidesToShow = useResponsiveSlides(sliderOptions)

  if (!products || products.length === 0) {
    return (
      <div className={containerClass} id={id}>
        <div className="row justify-content-center" />
      </div>
    )
  }

  const shouldUseSlider = products.length > currentSlidesToShow

  const settings = {
    arrows: true,
    prevArrow: <Arrow direction="prev" />,
    nextArrow: <Arrow direction="next" />,
    ...sliderOptions,
  }

  return (
    <div className={containerClass} id={id}>
      {shouldUseSlider ? (
        <Slider
          className={`cardSlider ${additionalClasses}`}
          items={products.map((p) => <CollectionCard key={p.id} {...p} />)}
          {...settings}
        />
      ) : (
        <div className="row justify-content-center">
          {products.map((product) => (
            <div className="col-md-2" key={product.id}>
              <CollectionCard {...product} />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

CollectionSlider.propTypes = {
  additionalClasses: PropTypes.string,
  containerClass: PropTypes.string,
  id: PropTypes.string,
  products: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string,
    price: PropTypes.string,
    image: PropTypes.string,
    imageAlt: PropTypes.string,
    url: PropTypes.string,
  })).isRequired,
  sliderOptions: PropTypes.shape({
    slidesToShow: PropTypes.number,
    responsive: PropTypes.arrayOf(PropTypes.shape({
      breakpoint: PropTypes.number,
      settings: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    })),
  }),
}

export default CollectionSlider
